import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Store } from "@ngxs/store";
import { of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AdministratorModel } from "src/app/shared/interfaces/administrator";
import {
  GET_ALERT_HISTORY_LIST,
  GET_E_ALERT_HISTORY_LIST,
  RESET_EMERGENCY_ALERT_STATE,
  RESET_LOGIN_ALERT_STATE,
  SAVE_EMERGENCY_ALERT,
  SAVE_LOGIN_ALERT,
  SET_EMERGENCY_ALERT_ARCHIVED,
  SET_LOGIN_ALERT_ARCHIVED,
  UPDATE_EMERGENCY_ALERT,
  UPDATE_LOGIN_ALERT,
} from "src/app/store/administrator/administrator.action";

import { AdministratorService } from "./administrator.service";

@State<AdministratorModel>({
  name: "AdministratorState",
  defaults: {
    loginAlert: {
      isLoginAlertSuccess: false,
      loginAlertErrormsg: null,
    },
    LoginAlertUpdated: {
      isLoginAlertSuccess: false,
      loginAlertErrormsg: null,
    },
    loginAlertArchived: {
      isLoginAlertSuccess: false,
      loginAlertErrormsg: null,
    },
    loginAlertHistoryLogList: [],
    emergencyAlertHistoryLogList: [],
    emergencyAlert: {
      isEmergencyAlertSuccess: false,
      emergencyAlertErrormsg: null,
    },
    emergencyAlertUpdated: {
      isEmergencyAlertSuccess: false,
      emergencyAlertErrormsg: null,
    },
    emergencyAlertArchived: {
      isEmergencyAlertSuccess: false,
      emergencyAlertErrormsg: null,
    },
  },
})
@Injectable()
export class AdministratorState {
  constructor(
    private readonly administratorService: AdministratorService,
    private readonly store: Store,
  ) {}

  @Selector()
  public static adminloginAlert(state: AdministratorModel) {
    return state.loginAlert;
  }
  @Selector()
  public static emergencyAlert(state: AdministratorModel) {
    return state.emergencyAlert;
  }

  @Selector()
  public static loginAlertUpdated(state: AdministratorModel) {
    return state.LoginAlertUpdated;
  }
  @Selector()
  public static emergencyAlertUpdated(state: AdministratorModel) {
    return state.emergencyAlertUpdated;
  }

  @Selector()
  public static adminloginAlertArchived(state: AdministratorModel) {
    return state.loginAlertArchived;
  }

  @Selector()
  public static loginAlertHistoryList(state: AdministratorModel) {
    return state.loginAlertHistoryLogList ? state.loginAlertHistoryLogList : [];
  }

  @Selector()
  public static emergencyAlertHistoryList(state: AdministratorModel) {
    return state.emergencyAlertHistoryLogList
      ? state.emergencyAlertHistoryLogList
      : [];
  }

  @Action(SAVE_LOGIN_ALERT)
  public saveLoginAlert(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: SAVE_LOGIN_ALERT,
  ) {
    return this.administratorService.saveAdminLoginAlert(payload).pipe(
      map((_res) => {
        if (_res?.saveSuccess === true) {
          patchState({
            loginAlert: {
              isLoginAlertSuccess: true,
              loginAlertErrormsg: null,
            },
          });
        } else {
          const errorMsg = _res?.error?.error ?? _res?.error ?? null;
          patchState({
            loginAlert: {
              isLoginAlertSuccess: false,
              loginAlertErrormsg: errorMsg,
            },
          });
        }
      }),
      catchError((err) => {
        const errorMsg = err?.error ?? err?.message ?? null;
        patchState({
          loginAlert: {
            isLoginAlertSuccess: false,
            loginAlertErrormsg: errorMsg,
          },
        });
        return of(null);
      }),
    );
  }

  @Action(UPDATE_LOGIN_ALERT)
  public updateLoginAlert(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: SAVE_LOGIN_ALERT,
  ) {
    return this.administratorService.saveAdminLoginAlert(payload).pipe(
      map((_res) => {
        if (_res?.saveSuccess === true) {
          patchState({
            LoginAlertUpdated: {
              isLoginAlertSuccess: true,
              loginAlertErrormsg: null,
            },
          });
        } else {
          patchState({
            LoginAlertUpdated: {
              isLoginAlertSuccess: false,
              loginAlertErrormsg: _res?.error?.error ?? _res?.error ?? null,
            },
          });
        }
      }),
      catchError((err) => {
        patchState({
          LoginAlertUpdated: {
            isLoginAlertSuccess: false,
            loginAlertErrormsg: err?.error ?? err?.message ?? null,
          },
        });
        return of(null);
      }),
    );
  }

  @Action(GET_ALERT_HISTORY_LIST)
  public getAlertHistoryList(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: GET_ALERT_HISTORY_LIST,
  ) {
    return this.administratorService.getAlertHistoryList(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            loginAlertHistoryLogList: res,
          });
        }
      }),
    );
  }

  @Action(SET_LOGIN_ALERT_ARCHIVED)
  public setLoginAlertArchived(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: GET_ALERT_HISTORY_LIST,
  ) {
    return this.administratorService.setLoginAlertArchived(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            loginAlertArchived: {
              isLoginAlertSuccess: true,
              loginAlertErrormsg: null,
            },
          });
        } else {
          patchState({
            loginAlertArchived: {
              isLoginAlertSuccess: false,
              loginAlertErrormsg: res.error,
            },
          });
        }
      }),
    );
  }

  @Action(SET_EMERGENCY_ALERT_ARCHIVED)
  public setEmergencyAlertArchived(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: GET_ALERT_HISTORY_LIST,
  ) {
    return this.administratorService.setEmergencyAlertArchived(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            emergencyAlertArchived: {
              isEmergencyAlertSuccess: true,
              emergencyAlertErrormsg: null,
            },
          });
        } else {
          patchState({
            emergencyAlertArchived: {
              isEmergencyAlertSuccess: false,
              emergencyAlertErrormsg: res.error,
            },
          });
        }
      }),
    );
  }

  @Action(RESET_LOGIN_ALERT_STATE)
  public resetLoginAlertState({
    patchState,
  }: StateContext<AdministratorModel>) {
    patchState({
      loginAlert: {
        isLoginAlertSuccess: false,
        loginAlertErrormsg: null,
      },
      LoginAlertUpdated: {
        isLoginAlertSuccess: false,
        loginAlertErrormsg: null,
      },
      loginAlertArchived: {
        isLoginAlertSuccess: false,
        loginAlertErrormsg: null,
      },
    });
  }

  @Action(GET_E_ALERT_HISTORY_LIST)
  public getEAlertHistoryList(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: GET_E_ALERT_HISTORY_LIST,
  ) {
    return this.administratorService.getAlertHistoryList(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            emergencyAlertHistoryLogList: res,
          });
        }
      }),
    );
  }

  @Action(SAVE_EMERGENCY_ALERT)
  public saveEmergencyAlert(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: SAVE_EMERGENCY_ALERT,
  ) {
    const userId = this.store.selectSnapshot<any>(
      (state) =>
        state.UserState?.user?.userId || state.SatUserState?.user?.userId,
    );

    return this.administratorService
      .saveAdminEmergencyAlert(payload, userId)
      .pipe(
        map((res) => {
          if (res.saveSuccess) {
            patchState({
              emergencyAlert: {
                isEmergencyAlertSuccess: res.saveSuccess,
                emergencyAlertErrormsg: null,
              },
            });
          } else {
            patchState({
              emergencyAlert: {
                isEmergencyAlertSuccess: false,
                emergencyAlertErrormsg: res.error,
              },
            });
          }
        }),
      );
  }

  @Action(UPDATE_EMERGENCY_ALERT)
  public updateEmergencyAlert(
    { patchState }: StateContext<AdministratorModel>,
    { payload }: SAVE_EMERGENCY_ALERT,
  ) {
    const userId = this.store.selectSnapshot<any>(
      (state) =>
        state.UserState?.user?.userId || state.SatUserState?.user?.userId,
    );

    return this.administratorService
      .saveAdminEmergencyAlert(payload, userId)
      .pipe(
        map((res) => {
          if (res.saveSuccess) {
            patchState({
              emergencyAlertUpdated: {
                isEmergencyAlertSuccess: res.saveSuccess,
                emergencyAlertErrormsg: null,
              },
            });
          } else {
            patchState({
              emergencyAlertUpdated: {
                isEmergencyAlertSuccess: false,
                emergencyAlertErrormsg: res.error,
              },
            });
          }
        }),
      );
  }

  @Action(RESET_EMERGENCY_ALERT_STATE)
  public resetEmergencyAlertState({
    patchState,
  }: StateContext<AdministratorModel>) {
    patchState({
      emergencyAlert: {
        isEmergencyAlertSuccess: false,
        emergencyAlertErrormsg: null,
      },
      emergencyAlertUpdated: {
        isEmergencyAlertSuccess: false,
        emergencyAlertErrormsg: null,
      },
      emergencyAlertArchived: {
        isEmergencyAlertSuccess: false,
        emergencyAlertErrormsg: null,
      },
    });
  }
}
